import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Eye, Split } from "lucide-react";
import { useTableSort, SortTh } from "@/lib/tableSort";
import { InfraFilters } from "@/pages/NetworkOdc";
import { ServerFilter } from "@/components/network/ServerFilter";
import { SplitterFormDialog } from "@/components/network/SplitterFormDialog";
import { SplitterDetail } from "@/components/network/SplitterDetail";
import { StatusPill, LocCell } from "@/components/network/StatusPill";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { useNetworkRefs } from "@/lib/network";
import { OldKodeLabel } from "@/components/network/OldKodeLabel";
import { InfraCards, InfraCard } from "@/components/network/InfraCards";

const inputLabel = (s) => {
  const i = s.input;
  if (!i) return "";
  if (i.source_kind === "splitter") return `Splitter ${i.source?.kode || "?"} · output ${i.source_output_port}`;
  if (i.source_kind && i.source_kind !== "pon") return `${i.source_kind.toUpperCase()} ${i.source?.kode || "?"}`;
  return `${i.olt?.kode || "?"} · PON ${i.port?.nama_port || "?"}`;
};

export default function NetworkSplitter() {
  const { refs, wilayahName } = useNetworkRefs();
  const [f, setF] = useState({ q: "", lokasi_id: "", wilayah_id: "", status: "", server_id: "" });
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get("/network/splitters", { params: f }).then((r) => setRows(r.data)), [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const getters = useMemo(() => ({ kode: (s) => s.kode, nama: (s) => s.nama, rasio: (s) => s.jumlah_output, terpakai: (s) => s.output_terpakai,
    input: inputLabel, wilayah: (s) => wilayahName(s.wilayah_id), status: (s) => s.status }), [wilayahName]);
  const st = useTableSort("splitter", rows, getters);
  const canManage = !!refs?.me?.can_manage;
  const cols = [["kode", "Kode"], ["nama", "Nama"], ["rasio", "Rasio"], ["terpakai", "Output"], ["input", "Input"], ["wilayah", "Wilayah"], ["status", "Status"]];
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="network-splitter-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Master Splitter</h1>
          <p className="text-sm text-[#6B7280]">Splitter optik: input dari PON/ODC/ODP/splitter → output ke ODC/ODP/splitter{canManage ? "" : " (mode lihat)"}</p></div>
        {canManage && <Button onClick={() => setForm({})} data-testid="splitter-add-btn"><Plus className="mr-1 h-4 w-4" />Tambah Splitter</Button>}
      </div>
      <InfraFilters refs={refs} f={f} setF={setF} prefix="splitter" placeholder="Cari kode, nama, posisi fisik…">
        <ServerFilter f={f} setF={setF} prefix="splitter" />
      </InfraFilters>
      <InfraCards prefix="splitter" st={st} cols={cols}
                  empty={rows && !rows.length ? `Belum ada splitter${Object.values(f).some(Boolean) ? " untuk filter ini" : ""}.` : null}
                  render={(s) => (
        <InfraCard key={s.id} prefix="splitter" row={s} q={f.q}
                   sub={<>{s.lokasi_fisik && <div>{s.lokasi_fisik}</div>}<span className={s.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(s.wilayah_id)}</span></>}
                   info={<><div>Rasio {s.rasio} · Output <span data-testid={`splitter-card-used-${s.kode}`}>{s.output_terpakai}/{s.jumlah_output}</span></div>
                     <div>Input: {s.input ? inputLabel(s) : <span className="text-[#B45309]" data-testid={`splitter-card-noinput-${s.kode}`}>Belum terhubung</span>}</div></>}
                   actions={<>
                     <Button size="icon" variant="ghost" onClick={() => setDetail(s.id)} data-testid={`splitter-card-view-${s.kode}`}><Eye className="h-4 w-4" /></Button>
                     {canManage && <Button size="icon" variant="ghost" onClick={() => setForm(s)} data-testid={`splitter-card-edit-${s.kode}`}><Pencil className="h-4 w-4" /></Button>}
                     <DeleteInfraButton iconOnly kind="splitter" item={s} me={refs?.me} onDeleted={load} tid={`splitter-card-delete-${s.kode}`} />
                   </>} />)} />
      <div className="hidden overflow-x-auto rounded-lg border border-[#E5E7EB] bg-white md:block">
        <table className="w-full min-w-[860px] text-sm" data-testid="splitter-table">
          <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6B7280]">
            <tr>{cols.map(([c, l], i) => <SortTh key={c} className={i ? "" : "p-2"} col={c} label={l} s={st} testid="splitter" />)}<th>Peta</th><th className="pr-2 text-right">Aksi</th></tr>
          </thead>
          <tbody>
            {(st.sorted || []).map((s) => (
              <tr key={s.id} className="border-t" data-testid={`splitter-row-${s.kode}`}>
                <td className="p-2 font-mono-tab font-semibold">{s.kode}<OldKodeLabel row={s} q={f.q} testid={`splitter-oldkode-${s.kode}`} /></td>
                <td>{s.nama}{s.lokasi_fisik && <div className="text-[11px] text-[#6B7280]">{s.lokasi_fisik}</div>}</td>
                <td>{s.rasio}</td><td data-testid={`splitter-used-${s.kode}`}>{s.output_terpakai}/{s.jumlah_output}</td>
                <td>{s.input ? inputLabel(s) : <span className="text-xs text-[#B45309]">Belum terhubung</span>}</td>
                <td className={s.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(s.wilayah_id)}</td>
                <td><StatusPill status={s.status} /></td>
                <td><LocCell row={s} testid={`splitter-nocoord-${s.kode}`} /></td>
                <td className="whitespace-nowrap pr-2 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setDetail(s.id)} data-testid={`splitter-view-${s.kode}`}><Eye className="h-4 w-4" /></Button>
                  {canManage && <Button size="icon" variant="ghost" onClick={() => setForm(s)} data-testid={`splitter-edit-${s.kode}`}><Pencil className="h-4 w-4" /></Button>}
                  <DeleteInfraButton iconOnly kind="splitter" item={s} me={refs?.me} onDeleted={load} />
                </td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={9} className="p-8 text-center text-[#6B7280]" data-testid="splitter-empty">
              <Split className="mx-auto mb-2 h-8 w-8 opacity-40" />Belum ada splitter{Object.values(f).some(Boolean) ? " untuk filter ini" : ""}.</td></tr>}
          </tbody>
        </table>
      </div>
      <SplitterFormDialog open={!!form} editing={form?.id ? form : null} refs={refs} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="splitter-detail-dialog">
          <DialogHeader><DialogTitle>Detail Splitter</DialogTitle></DialogHeader>
          {detail && <SplitterDetail splitterId={detail} canManage={canManage} me={refs?.me} wilayahName={wilayahName} onChanged={load}
                                     onDeleted={() => { setDetail(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
